import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ArrowBack } from '@mui/icons-material';
import {
  BoxSection,
  ButtonDefault,
  ButtonPrimary,
  Heading3,
} from 'app/components';
import { ApplicationRequestContext } from '../ApplicationRequest';
import { MagnifyingGassIcon } from 'app/components/Icons';
import { useTheme } from '@mui/material';
import { StyledButtonIcon } from 'app/components/SearchFilter/SearchInput/SearchInput.styles';
import {
  IFgtsMargin,
  mergeLoanGuaranteeInfoFromFgtsBalance,
} from './interfaces';
import EmptyState from 'app/components/EmptyState/EmptyState';
import AwaitingComponent from '../ConsignedStep/Awaiting/Awaiting';
import { StepContentButtonsArea } from '../styles';
import {
  StyledButtonWrapper,
  StyledContent,
  StyledWarningInfo,
} from './styles';
import FgtsMarginInfo from './FgtsMarginInfo/FgtsMarginInfo';
import { useApplicationsService } from 'modules/products/services/hooks';
import { useSnackbar } from 'app/hooks/useSnackbar';

const enum FgtsMarginSteps {
  LOADING = 'LOADING',
  INFO = 'INFO',
  ERROR = 'ERROR',
  EMPTY_STATE = 'EMPTY_STATE',
}

const ConsultaFgts = () => {
  const {
    onBack,
    onForward,
    consignedProduct,
    product,
    setLoanGuaranteeInfo,
    setLoanGuaranteeFormValues,
  } = useContext(ApplicationRequestContext);
  const { showSnackbar } = useSnackbar();
  const {
    getFgtsGuaranteeBalance,
    cancelFgtsGuaranteeBalancePolling,
    loadings,
  } = useApplicationsService();
  const theme = useTheme();
  const isConsultCancelledRef = useRef(false);

  const [currentStep, setCurrentStep] = useState<FgtsMarginSteps>(
    FgtsMarginSteps.EMPTY_STATE,
  );
  const [infoData, setInfoData] = useState<IFgtsMargin>();

  const getFgtsMargin = useCallback(async () => {
    const balanceCheckId = consignedProduct?.balance_check_id;
    const productId = product?.id;

    if (!balanceCheckId || !productId) {
      showSnackbar(
        'Não foi possível identificar o vínculo selecionado. Volte para a tela anterior, selecione um vínculo e tente novamente.',
        'error',
      );
      setCurrentStep(FgtsMarginSteps.ERROR);
      return;
    }

    isConsultCancelledRef.current = false;
    setCurrentStep(FgtsMarginSteps.LOADING);

    const response = await getFgtsGuaranteeBalance(balanceCheckId, productId);

    if (isConsultCancelledRef.current) {
      return;
    }

    if (
      response &&
      (response.available_fgts_guarantee_balance != null ||
        response.severance_fine_fgts_guarantee != null)
    ) {
      setInfoData(response);
      setLoanGuaranteeInfo((prev) =>
        mergeLoanGuaranteeInfoFromFgtsBalance(prev, response),
      );
      setLoanGuaranteeFormValues((prev) => ({
        ...prev,
        guarantee_balance_fgts_id: response.id ?? '',
      }));
      setCurrentStep(FgtsMarginSteps.INFO);
      return;
    }

    if (isConsultCancelledRef.current) {
      return;
    }

    setInfoData(undefined);
    setLoanGuaranteeInfo((prev) =>
      prev
        ? {
            ...prev,
            available_fgts_guarantee_balance: undefined,
            severance_fine_fgts_guarantee: undefined,
            guarantee_balance_fgts_id: undefined,
          }
        : undefined,
    );
    setCurrentStep(FgtsMarginSteps.ERROR);
  }, [
    consignedProduct?.balance_check_id,
    product?.id,
    getFgtsGuaranteeBalance,
    setLoanGuaranteeInfo,
    setLoanGuaranteeFormValues,
    showSnackbar,
  ]);

  const handleCancelConsult = useCallback(() => {
    isConsultCancelledRef.current = true;
    cancelFgtsGuaranteeBalancePolling();
    setCurrentStep(FgtsMarginSteps.EMPTY_STATE);
  }, [cancelFgtsGuaranteeBalancePolling]);

  useEffect(() => {
    if (consignedProduct?.balance_check_id && product?.id) {
      getFgtsMargin();
    }

    return () => {
      cancelFgtsGuaranteeBalancePolling();
    };
  }, []);

  const steps = {
    [FgtsMarginSteps.ERROR]: (
      <EmptyState
        subtitle="Sem a autorização não é possível consultar o saldo."
        title="O CPF consultado não permitiu o acesso às parcelas do FGTS."
        icon="secondary"
      />
    ),
    [FgtsMarginSteps.EMPTY_STATE]: (
      <EmptyState
        subtitle="Consulte o saldo novamente ou clique em avançar para seguir sem consultar o saldo do FGTS."
        title="Não foi possível realizar a consulta."
      />
    ),
    [FgtsMarginSteps.INFO]: <FgtsMarginInfo fgtsMargin={infoData} />,
    [FgtsMarginSteps.LOADING]: (
      <AwaitingComponent
        backStep={handleCancelConsult}
        hideSubtitle={loadings.isConsignedLoading}
        dataprevMessage={
          loadings.isDataprevPolling
            ? 'Processando consulta na Dataprev.'
            : undefined
        }
      />
    ),
  };

  useEffect(() => {
    if (isConsultCancelledRef.current) {
      return;
    }

    if (loadings.isConsignedLoading || loadings.isDataprevPolling) {
      setCurrentStep(FgtsMarginSteps.LOADING);
    }

    if (
      !loadings.isConsignedLoading &&
      !loadings.isDataprevPolling &&
      infoData
    ) {
      setCurrentStep(FgtsMarginSteps.INFO);
    }
  }, [infoData, loadings.isConsignedLoading, loadings.isDataprevPolling]);

  const hasConsultResult =
    infoData?.available_fgts_guarantee_balance != null ||
    infoData?.severance_fine_fgts_guarantee != null;

  return (
    <BoxSection>
      <StyledButtonWrapper>
        <div>
          <Heading3>Consulta de saldo FGTS</Heading3>
        </div>
        <ButtonPrimary
          onClick={getFgtsMargin}
          style={{ width: '15rem', marginLeft: '2rem' }}
          disabled={
            loadings.isConsignedLoading ||
            loadings.isDataprevPolling ||
            hasConsultResult
          }
        >
          Consultar saldo
          <StyledButtonIcon>
            <MagnifyingGassIcon
              color={theme.palette.brand.gray[100] as string}
            />
          </StyledButtonIcon>
        </ButtonPrimary>
      </StyledButtonWrapper>

      <StyledWarningInfo>
        O tomador precisa ter autorizado o acesso ao saldo do FGTS para que a
        solicitação possa ser processada.
      </StyledWarningInfo>

      <StyledContent>{steps[currentStep]}</StyledContent>

      <StepContentButtonsArea>
        <ButtonDefault margin={true} onClick={onBack}>
          <ArrowBack />
        </ButtonDefault>
        <ButtonPrimary onClick={onForward}>Avançar</ButtonPrimary>
      </StepContentButtonsArea>
    </BoxSection>
  );
};

export default ConsultaFgts;
